/**
 * BL-1912 — the Zod shape of a `MoneyWire` input (ADR 060 — Money Type § 2.3):
 * an exact decimal string that always travels with its currency. Shared by any
 * form or route that takes an amount, so the rules — never below the minimum,
 * never past the money ceiling of the amount's own currency (BUG-1903), never
 * more decimals than the column holds, or for a billed amount than the currency's
 * minor unit (BL-2904) — live in one place. Client-bundle-safe.
 */
import Decimal from 'decimal.js';

import { z } from '../zod';
import {
  MONEY_INPUT_MALFORMED_MESSAGE,
  MONEY_INPUT_NEGATIVE_MESSAGE,
  isSupportedCurrency,
  isTypedDecimal,
  parseMoneyInput,
  tooManyDecimalsMessage,
} from '../isomorphic/money';
import { decodeWireAmount } from '../mappers/realtime/money-wire-amount';

/**
 * The field message for an amount that is not a decimal string (or not a
 * MoneyWire at all — a bare number from a machine caller). One source, so every
 * MoneyWire field refuses malformed input in the same words (BL-2117).
 */
export const MONEY_WIRE_MALFORMED_MESSAGE = MONEY_INPUT_MALFORMED_MESSAGE;


/** Options for {@link moneyWireSchema}. */
export interface MoneyWireSchemaOptions {
  /**
   * Most digits allowed after the point: the storage column's scale, or
   * `'currency'` for an amount that must foot to printed lines and be payable, so
   * it stops at the amount's own minor unit (BL-2904: 2 for USD, 0 for JPY).
   * Trailing zeros are not precision: `40000.0000` and `¥1000.0000` pass either way.
   */
  maxScale: number | 'currency';
  /**
   * BL-2859 (ADR 076 — Money Input Text) — a leading minus is legal, for a field
   * whose negative is a real amount (the settlement's Record payment, a
   * collection). Defaults to false: a negative reads "Can't be negative".
   */
  allowNegative?: boolean;
  /**
   * Smallest amount allowed, when it is not simply "not negative" (the sign rule
   * above covers 0). Setting it takes over the sign rule's wording.
   */
  min?: number;
  /**
   * BL-2019 — refuse zero as well as negatives. A bid is never `0`; a price
   * (the BL-1912 lot inputs) can be, which is why this is opt-in.
   */
  positive?: boolean;
  /**
   * BL-2020 — the field's own copy for a zero / negative amount, when the form
   * already promised the operator one (the Sell to buyer form's BL-1095
   * "Sale price must be greater than zero."). Defaults to the generic message.
   * BL-2374: a function receives the amount's currency, so the copy can name
   * its zero ("Enter an amount above $0.00.").
   */
  positiveMessage?: PositiveMessage;
  /**
   * BL-2374 — read a blank amount as zero, so an empty box shows the
   * positive-amount copy rather than the malformed-amount one. Only with
   * `positive`. Defaults to false: a blank is malformed.
   */
  blankAsZero?: boolean;
}

/** A zero / negative amount's copy: fixed, or worded from the amount's currency. */
export type PositiveMessage = string | ((currency: string) => string);

/**
 * Build the Zod schema for one `MoneyWire` input. Every amount issue is
 * reported on the object itself (path `[]`), so a form shows it under the
 * field that holds the price rather than under a hidden `amount` key.
 *
 * @param opts See {@link MoneyWireSchemaOptions}.
 * @returns A schema that parses `{ amount, currency }` unchanged.
 * @sideEffect none — pure.
 */
export function moneyWireSchema(opts: MoneyWireSchemaOptions) {
  return z
    .object({ amount: z.string(), currency: z.string() })
    .superRefine((wire, ctx) => {
      const message = amountIssue({
        amount: wire.amount,
        currency: wire.currency,
        min: opts.min,
        allowNegative: opts.allowNegative ?? false,
        maxScale: opts.maxScale,
        positive: opts.positive ?? false,
        positiveMessage: opts.positiveMessage,
        blankAsZero: opts.blankAsZero ?? false,
      });
      if (message) ctx.addIssue({ code: 'custom', path: [], message });
      if (!isSupportedCurrency(wire.currency)) ctx.addIssue({ code: 'custom', path: [], message: 'Unsupported currency' });
    });
}

/**
 * The first thing wrong with an amount string, or null when it is valid.
 *
 * @returns The field message, or null.
 * @sideEffect none — pure.
 */
function amountIssue(input: {
  amount: string;
  currency: string;
  min?: number;
  allowNegative: boolean;
  maxScale: number | 'currency';
  positive: boolean;
  positiveMessage?: PositiveMessage;
  blankAsZero: boolean;
}): string | null {
  const { currency, min, maxScale, positive, positiveMessage } = input;
  const amount = input.blankAsZero && positive && input.amount.trim() === '' ? '0' : input.amount;
  // The parser's own shape rule ("5." and ".5" included), so the box and the server never disagree.
  if (!isTypedDecimal(amount)) return MONEY_WIRE_MALFORMED_MESSAGE;
  const value = new Decimal(amount);
  // BL-2859 (ADR 076): a minus on a no-negatives box reads the one sign message everywhere,
  // ahead of a field's own zero copy. The positive-only copy below speaks for zero and a blank.
  if (min == null && !input.allowNegative && value.lessThan(0)) return MONEY_INPUT_NEGATIVE_MESSAGE;
  if (positive && value.lessThanOrEqualTo(0)) {
    if (positiveMessage == null) return 'Enter an amount greater than 0';
    return typeof positiveMessage === 'string' ? positiveMessage : positiveMessage(currency);
  }
  if (min != null && value.lessThan(min)) return `Enter an amount of ${min} or more`;
  // An unsupported currency has its own refusal in the schema; the shared parser
  // judges the rest, so the ceiling and the places mean the same here as in the box.
  if (!isSupportedCurrency(currency)) return maxScale !== 'currency' && value.decimalPlaces() > maxScale ? tooManyDecimalsMessage(maxScale) : null;
  // BUG-1903: the ceiling and its copy are in the amount's own currency (¥9,999,999,999,
  // $9,999,999,999.99).
  // Left out, the parser's own default is the currency's places.
  const parsed = parseMoneyInput({ text: amount, currency, allowNegative: true, ...(maxScale === 'currency' ? {} : { decimalPlaces: maxScale }) });
  return parsed.ok ? null : parsed.message;
}

/** Storage scale of every `numeric(18,4)` money column (ADR 060 — Money Type § 2.1). */
const STORAGE_SCALE = 4;

/**
 * BL-2019 — the one request-side decoder for a MoneyWire amount: the
 * {@link moneyWireSchema} rules, then the shared `decodeWireAmount` (BL-2061), so a route's
 * `parseBody` hands its service a `Money` and nothing on the way decodes the
 * amount twice, by arithmetic, or through `Number()`. The scale cap is the
 * column's (4), not the currency's minor unit: an amount finer than the
 * currency (¥1,000.5) is the bid engine's to refuse as off the ladder, with its
 * own copy and nearest amounts, not a schema error.
 *
 * @param opts - `positive` refuses zero too (every bid amount); defaults to true.
 *   `positiveMessage` replaces the generic zero / negative copy (BL-2020), or
 *   words it from the currency (BL-2374). `blankAsZero` reads an empty box as zero.
 * @returns A Zod schema that parses `{ amount, currency }` into a `Money`.
 * @sideEffect none — pure.
 */
export function moneyWireAmountField(opts: { positive?: boolean; positiveMessage?: PositiveMessage; blankAsZero?: boolean } = {}) {
  return moneyWireSchema({
    maxScale: STORAGE_SCALE,
    positive: opts.positive ?? true,
    positiveMessage: opts.positiveMessage,
    blankAsZero: opts.blankAsZero,
  }).transform((wire, ctx) => {
    // The one MoneyWire → Money decode the payloads use too (BL-2061). The
    // refinements above already guarantee a decodable wire; the branch keeps
    // the decoder total rather than asserting.
    const decoded = decodeWireAmount(wire);
    if (decoded.kind === 'money') return decoded.money;
    ctx.addIssue({ code: 'custom', path: [], message: MONEY_WIRE_MALFORMED_MESSAGE });
    return z.NEVER;
  });
}
